'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { Link } from 'foundations-components/transitional/components/Link';
import { CardSection as CardSectionBase, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Flex } from 'foundations-components/components/Flex';
import { HighlyEngagedLeadIcon, KnowledgeBaseIcon } from 'foundations-assets';
import { getQuestionCopy } from '../../constants/quickAnswerQuestions';
import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import AIButton from 'ai-components-ui-library/components/buttons/AIButton';
import { getGatesAndScopes } from '../../selectors/gatesSelector';
import { trackEvent, EVENTS, ReportingTracker } from '../../lib/usageTracker';
import QuestionPill from './QuestionPill';
import ShowAllLessLink from './ShowAllLessLink';
import { useAnalyticsPermissions } from '.././../utils/useAnalyticsPermissions';
import { getHasAccessToSubApp, getHasAccessToTab } from '../../utils/reportingIAUtils';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CardSection = _ref => {
  let {
      children
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(CardSectionBase, Object.assign({
    padding: "400"
  }, props, {
    children: children
  }));
};
const FlexItemsContainer = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Flex, {
    flexWrap: "wrap",
    gap: "400",
    alignItems: "center",
    children: children
  });
};
const QuickAnswers = ({
  questions,
  onClickCallBack
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    gates,
    scopes
  } = useSelector(getGatesAndScopes);
  const analyticsPermissions = useAnalyticsPermissions();
  const openCopilot = useOpenCopilot();
  const questionCards = useMemo(() => {
    if (!questions) {
      return null;
    }
    const sortedCards = {
      popular: [],
      other: []
    };
    Object.keys(sortedCards).forEach(cardType => {
      const questionList = questions[cardType] || [];
      sortedCards[cardType] = questionList.reduce((questionPills, questionData) => {
        if (!questionData) {
          return questionPills;
        }
        const {
          questionId,
          subApp,
          contextOverrides
        } = questionData;
        const tab = contextOverrides === null || contextOverrides === void 0 ? void 0 : contextOverrides.tab;
        if (!getHasAccessToSubApp(subApp, analyticsPermissions)) {
          return questionPills;
        } else if (tab && !getHasAccessToTab(subApp, tab, gates, scopes)) {
          return questionPills;
        }

        /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
        return questionPills.concat([/*#__PURE__*/_jsx(QuestionPill, Object.assign({
          id: questionId,
          title: getQuestionCopy(questionId) || questionId,
          icon: cardType === 'popular' ? HighlyEngagedLeadIcon : null
        }, questionData.path && {
          onClick: () => onClickCallBack({
            question: questionData
          })
        }), questionId)]);
      }, []);
    });
    return sortedCards;
  }, [questions, onClickCallBack, analyticsPermissions, gates, scopes]);
  const toggleExpandItem = e => {
    setIsExpanded(current => !current);
    e.preventDefault();
  };
  if (!questionCards) {
    return null;
  }
  return /*#__PURE__*/_jsxs(CardWrapper, {
    marginBlockStart: "500",
    marginBlockEnd: "700",
    compact: true,
    children: [/*#__PURE__*/_jsxs(CardSection, {
      "data-test-id": "quick-answers-card-section",
      children: [/*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        alignItems: "center",
        inlineSize: "100%",
        paddingBlockEnd: "400",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h6",
            textTransform: "uppercase",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'reports.collectionHomes.quickAnswers.header'
            })
          }), /*#__PURE__*/_jsxs(Text, {
            as: "small",
            color: "text-core-subtle",
            children: [/*#__PURE__*/_jsx(IconHolder, {
              marginInlineStart: "300",
              marginInlineEnd: "100",
              padding: "0",
              icon: HighlyEngagedLeadIcon
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'reports.collectionHomes.quickAnswers.mostPopularKey'
            })]
          })]
        }), /*#__PURE__*/_jsx(ShowAllLessLink, {
          isExpanded: isExpanded,
          onClick: toggleExpandItem,
          itemCount: questionCards.popular.length + questionCards.other.length
        })]
      }), /*#__PURE__*/_jsx(FlexItemsContainer, {
        children: questionCards.popular
      })]
    }), /*#__PURE__*/_jsxs(AccordionTransition, {
      open: isExpanded,
      "data-test-id": "quick-answers-accordian",
      children: [/*#__PURE__*/_jsx(CardSection, {
        paddingBlockStart: "0",
        children: /*#__PURE__*/_jsx(FlexItemsContainer, {
          children: questionCards.other
        })
      }), /*#__PURE__*/_jsx(CardSection, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "h6",
          textTransform: "uppercase",
          paddingBlockStart: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'reports.collectionHomes.quickAnswers.footer'
          })
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        flexWrap: "wrap",
        gap: "400",
        alignItems: "center",
        paddingInlineStart: "100",
        paddingBlockEnd: "400",
        children: [/*#__PURE__*/_jsx(AIButton, {
          use: "transparent",
          onClick: () => {
            trackEvent(EVENTS.clickOpenCopilot);
            openCopilot();
          },
          usageTracker: ReportingTracker,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiComponentsUiLibrary.components.copilotActionButton.tooltipCopilot"
          })
        }), /*#__PURE__*/_jsxs(Link, {
          external: true,
          onClick: () => {
            trackEvent(EVENTS.clickHelpCenter);
          },
          href: "https://help.hubspot.com",
          children: [/*#__PURE__*/_jsx(IconHolder, {
            icon: KnowledgeBaseIcon,
            color: "icon-interactive-default",
            marginInlineEnd: "100",
            padding: "0"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'reports.collectionHomes.quickAnswers.helpCenterLink'
          })]
        })]
      })]
    })]
  });
};
export default QuickAnswers;